import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { EditorForm } from "./shared/EditorForm";
import { EditorField } from "./shared/EditorField";
import { EditorHeader } from "./EditorHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { PipelineEditor } from "./PipelineEditor";

export const TractionEditor = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingMetric, setEditingMetric] = useState<any>(null);
  const { toast } = useToast();

  const { data: metrics, refetch: refetchMetrics } = useQuery({
    queryKey: ['traction-metrics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('traction_metrics')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      const data = {
        metric_name: String(formData.get('metric_name')),
        value: String(formData.get('value')),
        category: String(formData.get('category')),
        description: formData.get('description') ? String(formData.get('description')) : null
      };

      let error;
      
      if (editingMetric?.id) {
        ({ error } = await supabase
          .from('traction_metrics')
          .update(data)
          .eq('id', editingMetric.id));
      } else {
        ({ error } = await supabase
          .from('traction_metrics')
          .insert([data]));
      }

      if (error) throw error;

      toast({
        title: "Success",
        description: editingMetric?.id 
          ? "Traction metric updated successfully"
          : "Traction metric added successfully",
      });
      
      setEditingMetric(null);
      refetchMetrics();
      e.currentTarget.reset();
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to save traction metric",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (metric: any) => {
    setEditingMetric(metric);
  };

  const handleCancel = () => {
    setEditingMetric(null);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('traction_metrics')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Traction metric deleted successfully",
      });

      refetchMetrics();
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to delete traction metric",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <EditorHeader 
        title="Traction & Pipeline Editor"
        description="Manage traction metrics and pipeline items"
      />
      
      <Tabs defaultValue="metrics" className="w-full">
        <TabsList>
          <TabsTrigger value="metrics">Traction Metrics</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
        </TabsList>

        <TabsContent value="metrics">
          <EditorForm onSubmit={handleSubmit} isSubmitting={isSubmitting}>
            <div className="space-y-4">
              <EditorField
                label="Metric Name"
                name="metric_name"
                type="text"
                required
                defaultValue={editingMetric?.metric_name}
              />
              <EditorField
                label="Value"
                name="value"
                type="text"
                required
                defaultValue={editingMetric?.value}
              />
              <EditorField
                label="Category"
                name="category"
                type="text"
                required
                defaultValue={editingMetric?.category}
              />
              <EditorField
                label="Description"
                name="description"
                type="textarea"
                defaultValue={editingMetric?.description}
              />
            </div>
            {editingMetric && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancel}
                className="mt-2"
              >
                Cancel Edit
              </Button>
            )}
          </EditorForm>

          <div className="mt-8 space-y-4">
            {metrics?.map((metric) => (
              <Card key={metric.id} className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">{metric.metric_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      Value: {metric.value} | Category: {metric.category}
                    </p>
                    {metric.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {metric.description}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(metric)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(metric.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="pipeline">
          <PipelineEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
};